import {
  FluidFormElementState,
  FluidKeyedObject,
  FluidLanguage,
  FluidPopoverConfig,
  FluidProgressState,
  FluidTheme,
  FluidThemeInterface,
  FluidToggleConfig,
  FluidToggleType,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { FluidToggleGroupTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-element-translations.type';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { FormElementWrapper } from '../../common/fluid-common-form.components';
import { TOGGLE_GROUP_INVALID_STATE } from '../../constants/constants';
import {
  setI18nState,
  translateProperty,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { toControlName } from '../../utils/forms/form-method.utils';
import { Utils } from '../../utils/utils';
import {
  ToggleAll,
  ToggleAllValue,
  ToggleGroupToggle,
} from './components/fluid-toggle-group.components';

/**
 * @displayName Toggle Group
 *
 * @contextData Using toggles is a good option when you want to give your visitors the option to choose several items from a group of choices.
 * In that regard, the toggle group works opposite of a radio group, which only allows you to select one item from a group of choices. The FLUID
 * toggle group allows you to provide a list of toggle options, which will render in a group, and using the toggleState event, will tell the calling
 * component the full state of the toggle group when something changes. Additionally, by adding a toggleAllLabel, FLUID automatically provides a 'Toggle
 * All' option, which will control the entire group!
 *
 * @advancedUsage /forms#toggleGroup
 */
@Component({
  tag: 'fluid-toggle-group',
  styleUrl: 'fluid-toggle-group.scss',
  shadow: true,
})
export class FluidToggleGroup
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Object that stores references to each individual Toggle within the
   * group. Each key is the Toggle's "id", and each value is the Toggle
   * element reference.
   */
  // @ts-ignore - will be used when Toggle Group is supported as a Form Element
  private _toggleRefs: FluidKeyedObject<HTMLFluidToggleElement> = {};

  /**
   * Reference to the "Toggle All" Toggle element.
   */
  // @ts-ignore - will be used when Toggle Group is supported as a Form Element
  private _toggleAllRef: HTMLFluidToggleElement;

  /**
   * The FluidToggleType of the "Toggle All" Toggle. Set via
   * "groupToggleType" first, then by deprecated "toggleAllType" (if
   * "groupToggleType" undefined).
   */
  private _toggleAllType: FluidToggleType;

  /**
   * Internal copy of "toggleOptions" Prop WITHOUT any properties that
   * would affect the "checked" state of the group's Toggles (i.e.
   * does NOT have "initialValue" or "toggled" properties). Used to
   * render the group's Toggles, as well as to retain Toggles that
   * should be "carried over" when "toggleOptions" Prop changes (i.e.
   * Toggles with the same "id" or "controlName" property).
   */
  _toggleOptions: FluidToggleConfig[] = [];

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element
   */
  @Element() host: HTMLFluidToggleGroupElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidToggleGroupTranslationOptions;
  private _translateId: string;
  @State() _i18nStore: FluidI18nStore | undefined;
  @State() _lang: FluidLanguage = FluidLanguage.ENGLISH;

  // ================================================================ //
  // -- States

  /**
   * Object that tracks the "checked" state of all Toggles within the
   * Toggle Group. Each key is an individual Toggle's `controlName`,
   * and each value is the "checked" state (value) of the Toggle.
   */
  @State() _toggleState: FluidKeyedObject<ToggleAllValue> = {};

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If provided, a FLUID Form Label will be rendered above the Toggle
   * Group with the specified string.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * When provided, Toggle Group's Form Label will render a "helptext"
   * Icon with attached Popover. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * Sets the "toggleType" Prop for all Toggles within the group
   * (including the "Toggle All" Toggle). Accepts: "checkbox", "switch",
   * "toggle-chip" (chip with checkbox), or "chip" (no checkbox).
   */
  @Prop({ mutable: true }) groupToggleType: Extract<
    FluidToggleType,
    | FluidToggleType.CHECKBOX
    | FluidToggleType.SWITCH
    | FluidToggleType.TOGGLE_CHIP
    | FluidToggleType.CHIP
  >;

  @Watch('groupToggleType')
  handleGroupToggleTypeChange() {
    if (isNullOrUndefined(this.groupToggleType) || !this._toggleOptions?.length)
      return;

    this._toggleAllType = this.groupToggleType;
    this._toggleOptions = this._toggleOptions.map((opt: FluidToggleConfig) => ({
      ...opt,
      toggleType: this.groupToggleType,
    }));
  }

  /**
   * Array of FluidToggleConfig objects that populates the Toggle Group.
   * Each object should include the "controlName" property to identify
   * the Toggle within the group (if undefined, "controlName" will be
   * created from the "label" property).
   */
  @Prop({ mutable: true }) toggleOptions: FluidToggleConfig[];

  @Watch('toggleOptions')
  handleToggleOptionsChange(
    newToggles: FluidToggleConfig[],
    oldToggles: FluidToggleConfig[]
  ) {
    if (Utils.isEqual(newToggles, oldToggles)) return;
    !oldToggles?.length ? this.initializeState() : this.updateState();
  }

  /**
   * If supplied, a "Toggle All" Toggle that controls the "checked" value
   * of all Toggles within the group will be rendered first.
   */
  @Prop({ mutable: true }) toggleAllLabel = '';

  /**
   * If a "toggleAllLabel" is supplied, what should the initial value
   * be? This will determine the state of ALL Toggles on initialization.
   */
  @Prop({ mutable: true }) toggleAllValue: ToggleAllValue = true;

  @Watch('toggleAllValue')
  handleToggleAllValueChange() {
    if (this.toggleAllValueEqualsState()) return;
    this.toggleAll(this.toggleAllValue);
  }

  /**
   * If true, Toggles will cascade horizontally instead of vertically.
   */
  @Prop({ mutable: true }) horizontalLayout = false;

  /**
   * When false, Toggle Group will have additional spacing added to
   * the bottom of the component.
   */
  @Prop({ mutable: true }) inlineField = true;

  /**
   * If true, all Toggles within the group will be disabled (including
   * "Toggle All" Toggle).
   */
  @Prop({ mutable: true }) disabled: boolean;

  // ================================================================ //
  // -- Events

  /**
   * Emits the state of the Toggle Group with each Toggle change.
   * The emitted element takes the form of an object such that each
   * key is the `controlName`, and the value is the value of the Toggle
   * (i.e. { <controlName>: <toggleValue> }).
   */
  @Event() toggleState: EventEmitter<FluidKeyedObject<ToggleAllValue>>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: initializeState - create initial _toggleState
   */
  componentWillLoad() {
    this.setToggleAllType();
    this.initializeState();
  }

  /**
   * Called when Prop/State changes & re-render is about to be requested.
   * When "false" is explicitly returned, the component will NOT re-render.
   * When "true" OR "undefined" is returned, the component WILL re-render.
   */
  componentShouldUpdate(
    newValue: any,
    oldValue: any,
    propName: string
  ): boolean | void {
    // When "toggleOptions" Prop changes, only trigger re-render if necessary
    if (propName === 'toggleOptions') {
      return !Utils.isEqual(newValue, oldValue);
    }
    // When any other Prop/State changes, always trigger re-render (i.e. return "undefined")
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * It's never called during the first render().
   * @action updateToggleAllValue - updates "toggleAllValue" Prop (if necessary)
   */
  componentWillUpdate() {
    this.updateToggleAllValue();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Event listener for "focusout" events. Triggered when ALL Toggle
   * Group descendants lose :focus. This is distinct from a "blur"
   * listener in that it isn't triggered on INTERNAL blur events fired
   * when :focus changes between Toggles.
   */
  @Listen('focusout')
  onFocusOut() {
    // TODO: run "markTouchedAndValidate()"
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Changes "checked" value of all Toggles within the Toggle Group
   * & emits updated toggleState.
   */
  @Method()
  async toggleAll(toggled?: ToggleAllValue) {
    this._toggleState = this._toggleOptions.reduce(
      (state: FluidKeyedObject<boolean>, config: FluidToggleConfig) => ({
        ...state,
        [config.controlName]: toggled ?? false,
      }),
      {}
    );
    this.toggleState.emit(this._toggleState);
  }

  /**
   * Sets the value of each individual Toggle within the Toggle Group.
   * The "toggleState" param accepts an object where each key is the
   * "controlName" of a Toggle, and each value is the Toggle's new
   * value (i.e. "checked" state). NOTE: All "controlName" keys in
   * the "toggleState" param MUST match up to the "controlName"
   * properties of existing Toggles within the group.
   * @param toggleState - object with Toggle "controlName" keys & boolean values
   */
  @Method()
  async setToggleState(toggleState: FluidKeyedObject<boolean>) {
    const controlNames = Object.keys(this._toggleState);
    const newControlNames = Object.keys(toggleState);

    if (
      controlNames.length !== newControlNames.length ||
      !controlNames.every((name) => newControlNames.includes(name))
    ) {
      Utils.consoleWarn(this.host, TOGGLE_GROUP_INVALID_STATE);
      return;
    }

    this._toggleState = controlNames.reduce(
      (state: FluidKeyedObject<boolean>, controlName: string) => ({
        ...state,
        [controlName]: toggleState[controlName],
      }),
      {}
    );
    this.toggleState.emit(this._toggleState);
  }

  /**
   * Returns the current "checked" state of all Toggles within the
   * group. The returned object has the same structure as the
   * "toggleState" Event, where each key is the "controlName" of
   * a Toggle & each value is the Toggle's current value (i.e.
   * "checked" state).
   */
  @Method()
  async getToggleState(): Promise<FluidKeyedObject<ToggleAllValue>> {
    return this._toggleState;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Returns true if the "Toggle All" Toggle is configured, false otherwise.
   */
  hasToggleAll() {
    return !!this.toggleAllLabel || !!this.fluidTranslate?.toggleAllLabel;
  }

  /**
   * Formats a FluidToggleConfig object (from "toggleOptions" Prop) for
   * use within the Toggle Group. If "matchExistingId" is true, the
   * method will attempt to match the "id" property of the config with
   * an existing Toggle's "id" property in "_toggleOptions" (since "id"
   * is used as the "key" attribute for each Toggle, retaining a Toggle's
   * "id" prevents it from being destroyed/replaced during re-render).
   * @param config - FluidToggleConfig object from "toggleOptions" Prop
   * @param matchExistingId - attempt to retain "id" property from existing Toggles
   */
  formatToggleConfig(
    config: FluidToggleConfig,
    matchExistingId = false
  ): FluidToggleConfig {
    if (!config) return;

    const controlName = config.controlName || toControlName(config.label);
    const id =
      config.id ||
      (matchExistingId &&
        this._toggleOptions?.find((c) => c.controlName === controlName)?.id) ||
      generateUniqueId();

    return {
      ...config,
      id,
      controlName,
      // If "groupToggleType" is defined, all Toggles have the same "toggleType"
      ...(!!this.groupToggleType && { toggleType: this.groupToggleType }),
      // Configure Toggle as menu item if "asMenuSection" is true
      ...(this.asMenuSection && { asMenuItem: true }),
    };
  }

  /**
   * Initializes "_toggleOptions" & "_toggleState" on first load OR when
   * "toggleOptions" Prop is populated (from undefined or []), then emits
   * the initial state of the Toggle Group.
   */
  initializeState() {
    if (!this.toggleOptions?.length) return;

    this._toggleRefs = {};
    this._toggleOptions = this.toggleOptions.map(
      (tglConfig: FluidToggleConfig) => {
        // Remove Props that affect "checked" state ("checked" status handled by "_toggleState")
        const { initialValue, toggled, ...props } = { ...tglConfig };
        const config = this.formatToggleConfig(props);

        switch (true) {
          // First, check if "Toggle All" is configured AND "toggleAllValue" is true
          case this.hasToggleAll() && this.toggleAllValue:
            this._toggleState[config.controlName] = true;
            break;
          // Next, "initialValue" takes precedence over "toggled" on first load
          case !isNullOrUndefined(initialValue):
            this._toggleState[config.controlName] = !!initialValue;
            break;
          // If "Toggle All" is undefined/false AND "initialValue" is undefined, use "toggled"
          case !isNullOrUndefined(toggled):
            this._toggleState[config.controlName] = !!toggled;
            break;
          // If none of the previous conditions are met, set state to "unchecked"
          default:
            this._toggleState[config.controlName] = false;
        }

        return config;
      }
    );

    // Update "toggleAllValue" Prop to match group's current state
    this.updateToggleAllValue();
    this.toggleState.emit(this._toggleState);
  }

  /**
   * Updates "_toggleOptions" & "_toggleState" when "toggleOptions" Prop
   * is updated AFTER first load. Retains the VALUE of Toggles that have
   * the same "controlName" property as existing Toggles, UNLESS overridden
   * with the "toggled" property. Also retains the ID property of existing
   * Toggles (i.e. when "id" was generated) to prevent them from being
   * destroyed/replaced during re-render.
   */
  updateState() {
    this._toggleRefs = {};
    if (!this.toggleOptions?.length) {
      this._toggleOptions = [];
      this._toggleState = {};
      return;
    }

    const existingToggleState = { ...this._toggleState };
    this._toggleState = {};
    this._toggleOptions = this.toggleOptions.map(
      (tglConfig: FluidToggleConfig) => {
        // Remove Props that affect "checked" state ("checked" status handled by "_toggleState")
        const { initialValue, toggled, ...props } = { ...tglConfig };

        const config = this.formatToggleConfig(props, true);
        const existingValue = existingToggleState[config.controlName];

        switch (true) {
          // If "toggled" provided, use its value (regardless of whether Toggle already exists)
          case !isNullOrUndefined(toggled):
            this._toggleState[config.controlName] = !!toggled;
            break;
          // If Toggle already exists AND "toggled" not explicitly provided, retain its value
          case !isNullOrUndefined(existingValue):
            this._toggleState[config.controlName] = existingValue;
            break;
          // If provided, use "initialValue" for NEW Toggles (existing Toggles only use "toggled" Prop)
          case !isNullOrUndefined(initialValue):
            this._toggleState[config.controlName] = !!initialValue;
            break;
          // If none of the previous conditions are met, set state to "unchecked"
          default:
            this._toggleState[config.controlName] = false;
        }

        return config;
      }
    );
  }

  /**
   * Determines if the "toggleAllValue" Prop matches the current value
   * of the "Toggle All" Toggle. Returns true if "Toggle All" is NOT
   * configured (i.e. "toggleAllValue" is irrelevant), or if the Prop's
   * value already matches the "Toggle All" Toggle's value ("indeterminate"
   * state is considered a match if "toggleAllValue" is undefined).
   */
  toggleAllValueEqualsState() {
    const value = this.getToggleAllValue();
    return (
      !this.hasToggleAll() ||
      value === this.toggleAllValue ||
      (value === FluidProgressState.INDETERMINATE &&
        this.toggleAllValue === undefined)
    );
  }

  /**
   * Dynamically updates the "toggleAllValue" Prop to match the "Toggle
   * All" Toggle's current value (since Prop is boolean type, sets as
   * undefined if the "Toggle All" Toggle is indeterminate). Called every
   * time the component updates/re-renders.
   */
  updateToggleAllValue() {
    if (this.toggleAllValueEqualsState()) return;
    const value = this.getToggleAllValue();
    this.toggleAllValue =
      value === FluidProgressState.INDETERMINATE ? undefined : value;
  }

  /**
   * Updates & emits Toggle Group's internal state when the
   * "Toggle All" Toggle changes (which also updates the values of
   * each individual Toggle within the group). Called each time the
   * "Toggle All" Toggle's value changes.
   * @param toggleAllState - "state" object emitted from Toggle when value changes
   */
  handleToggleAllChange(toggleAllState: FluidFormElementState) {
    const { value } = toggleAllState;
    this._toggleState = this._toggleOptions.reduce(
      (state: FluidKeyedObject<boolean>, config: FluidToggleConfig) => ({
        ...state,
        [config.controlName]: value as boolean,
      }),
      {}
    );
    this.toggleState.emit(this._toggleState);
  }

  /**
   * Updates & emits Toggle Group's internal state when one of the
   * group's Toggles changes. Called each time a "standard" Toggle's
   * value changes (i.e. NOT the "Toggle All" Toggle).
   * @param toggleState - "state" object emitted from Toggle when value changes
   */
  handleToggleChange(toggleState: FluidFormElementState) {
    const { name, value } = toggleState;
    this._toggleState = {
      ...this._toggleState,
      [name]: value as boolean,
    };
    this.toggleState.emit(this._toggleState);
  }

  /**
   * Returns the correct value for the "Toggle All" Toggle given the
   * current state of the group's Toggles (e.g. true if all Toggles
   * are "checked", false if all Toggles are "unchecked"). If the
   * group's Toggles have differing values, returns "indeterminate"
   * when "Toggle All" is a checkbox, or false if a switch.
   */
  getToggleAllValue() {
    const valueSet = new Set(Object.values(this._toggleState));
    if (valueSet.size === 1) {
      return valueSet.has(true);
    } else {
      return this._toggleAllType === FluidToggleType.CHECKBOX
        ? FluidProgressState.INDETERMINATE
        : false;
    }
  }

  /**
   * Creates & returns the "config" object for the "Toggle All" Toggle.
   * @return - FluidToggleConfig object for "Toggle All" Toggle
   */
  getToggleAllConfig(): FluidToggleConfig {
    if (!this.hasToggleAll()) return;
    return {
      controlName: 'toggleAll',
      disabled: this.disabled,
      initialValue: this.toggleAllValue || this.getToggleAllValue(),
      toggled: this.getToggleAllValue(),
      label: translateProperty.bind(this)('toggleAllLabel') || 'Toggle All',
      toggleType: this._toggleAllType,
      inlineField: !this.asMenuSection,
      asMenuItem: this.asMenuSection,
    };
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (!this._toggleOptions?.length) return;

    return (
      <FormElementWrapper
        host={this}
        formLabelProps={{
          resetPosition: true,
          class: 'toggle-group-label',
        }}>
        <div
          role='group'
          aria-label='Toggle Group'
          class={{
            [`group-type-${this.groupToggleType}`]: !!this.groupToggleType,
            'horizontal-layout': this.horizontalLayout && !this.asMenuSection,
            'menu-section': this.asMenuSection,
          }}>
          <ToggleAll
            config={this.getToggleAllConfig()}
            theme={this.theme}
            setRef={(el: HTMLFluidToggleElement) => (this._toggleAllRef = el)}
            handleToggleAllChange={(state: FluidFormElementState) =>
              this.handleToggleAllChange(state)
            }
          />
          {this._toggleOptions.map((config: FluidToggleConfig) => (
            <ToggleGroupToggle
              config={config}
              toggled={this._toggleState[config.controlName]}
              disabled={this.disabled}
              theme={this.theme}
              setRef={(el: HTMLFluidToggleElement) =>
                (this._toggleRefs[config.id] = el)
              }
              handleToggleChange={(state: FluidFormElementState) =>
                this.handleToggleChange(state)
              }
            />
          ))}
        </div>
      </FormElementWrapper>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * If true, Toggles are formatted for use within Context Menu.
   * @internal
   */
  @Prop({ mutable: true }) asMenuSection = false;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "toggleAllType" is now deprecated - the "Toggle All"
   * Toggle type should be consistent with the rest of the group's
   * Toggles. Instead, use the "groupToggleType" Prop to set the type
   * of ALL Toggles within the group. -- What type of Toggle should we
   * use for the "Toggle All" Toggle?
   * @deprecated
   */
  @Prop({ mutable: true }) toggleAllType: FluidToggleType;

  @Watch('toggleAllType')
  setToggleAllType() {
    switch (true) {
      case !isNullOrUndefined(this.groupToggleType):
        this._toggleAllType = this.groupToggleType;
        break;
      case !isNullOrUndefined(this.toggleAllType):
        this._toggleAllType = this.toggleAllType;
        Utils.deprecationWarning(
          this.host,
          'Prop',
          'toggleAllType',
          'groupToggleType',
          'FluidToggleType',
          null,
          'The "Toggle All" Toggle type should be consistent with the rest of the group\'s Toggles - use "groupToggleType" Prop to set the type of ALL Toggles within the group.'
        );
        break;
      default:
        this._toggleAllType = FluidToggleType.CHECKBOX;
    }
  }
}
